import { useEffect, useRef, useState } from 'react';
import { LogIn, LogOut, Loader2, MapPin, MapPinOff } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import {
    captureCurrentPosition,
    useCheckIn,
    useCheckOut,
    useSessionEvv,
} from '@/api/evv';

/*
 * EvvControls — the RBT-facing check-in / check-out widget for a session.
 *
 * Rendered on SessionPage's Detail tab only, and only when:
 *   - settings.scheduling.evvEnabled is on
 *   - the caller has view_scheduling (the gate the server enforces on
 *     POST /api/sessions/:id/check-in and /check-out)
 *   - the session has a client (administrative sessions aren't visits)
 *   - the session status isn't terminal (Finalized / Canceled / No Show)
 *
 * Geolocation is best-effort — captureCurrentPosition resolves null on deny
 * or timeout and the check-in proceeds anyway. The server derives
 * Unverified status on checkout if GPS is missing on either end. We don't
 * block the RBT from doing the visit because a sensor misbehaved.
 */

interface EvvControlsProps {
    sessionId: string;
    sessionStatus: string;
}

const TERMINAL_STATUSES = new Set(['Finalized', 'Canceled', 'Cancelled', 'No Show']);

const COOLDOWN_MS = 2000;

export function EvvControls({ sessionId, sessionStatus }: EvvControlsProps) {
    const evvQuery = useSessionEvv(sessionId);
    const checkInM = useCheckIn(sessionId);
    const checkOutM = useCheckOut(sessionId);
    const [error, setError] = useState<string | null>(null);

    // Lock the whole widget from the moment a button is clicked until a short
    // window after the operation completes. This matters because:
    //   1. captureCurrentPosition() can take up to 8s before the mutation
    //      even starts, so checkInM.isPending alone doesn't cover the GPS
    //      wait — the button would be double-clickable during that window.
    //   2. On success the primary action swaps (Check In → Check Out).
    //      Without a cooldown, a trailing click from a laggy tap lands on
    //      the wrong action — and an accidental check-out has no undo path.
    const [cooling, setCooling] = useState(false);
    const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => () => {
        if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    }, []);

    function startCooldown() {
        if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
        setCooling(true);
        cooldownTimer.current = setTimeout(() => setCooling(false), COOLDOWN_MS);
    }

    if (TERMINAL_STATUSES.has(sessionStatus)) return null;
    if (evvQuery.isLoading) {
        return (
            <Card className="mb-3 px-3 py-2 flex items-center gap-2 text-xs text-text-subtle">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading visit status…
            </Card>
        );
    }

    const record = evvQuery.data ?? null;
    const hasCheckIn = !!record?.checkIn;
    const hasCheckOut = !!record?.checkOut;

    async function handleCheckIn() {
        setError(null);
        setCooling(true);
        const coords = await captureCurrentPosition();
        try {
            await checkInM.mutateAsync({ lat: coords?.lat ?? null, lng: coords?.lng ?? null });
        } catch (err) {
            // 409 = session already has an EVV row. onSettled just refetched
            // the session query, so the UI will now show the existing state —
            // no need to scare the user with a red alert.
            if (!(axios.isAxiosError(err) && err.response?.status === 409)) {
                setError(extractErrorMessage(err, 'Check-in failed.'));
            }
        } finally {
            startCooldown();
        }
    }

    async function handleCheckOut() {
        setError(null);
        setCooling(true);
        const coords = await captureCurrentPosition();
        try {
            await checkOutM.mutateAsync({ lat: coords?.lat ?? null, lng: coords?.lng ?? null });
        } catch (err) {
            if (!(axios.isAxiosError(err) && err.response?.status === 409)) {
                setError(extractErrorMessage(err, 'Check-out failed.'));
            }
        } finally {
            startCooldown();
        }
    }

    return (
        <Card className="mb-3 p-3 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 text-xs">
                    <div className="text-2xs font-bold uppercase tracking-wider text-text-subtle">Visit</div>
                    {record ? <StatusBadge status={record.status} /> : <span className="text-text-subtle">Not started</span>}
                    {hasCheckIn && (
                        <div className="flex items-center gap-1">
                            <span className="text-text-muted">In:</span>
                            <span className="tabular-nums">{formatTs(record!.checkIn)}</span>
                            {record!.gpsIn
                                ? <MapPin className="w-3 h-3 text-success-text" aria-label="GPS captured" />
                                : <MapPinOff className="w-3 h-3 text-warning-text" aria-label="No GPS" />}
                        </div>
                    )}
                    {hasCheckOut && (
                        <div className="flex items-center gap-1">
                            <span className="text-text-muted">Out:</span>
                            <span className="tabular-nums">{formatTs(record!.checkOut)}</span>
                            {record!.gpsOut
                                ? <MapPin className="w-3 h-3 text-success-text" aria-label="GPS captured" />
                                : <MapPinOff className="w-3 h-3 text-warning-text" aria-label="No GPS" />}
                        </div>
                    )}
                    {hasCheckOut && record!.durMin > 0 && (
                        <span className="text-text-muted">{record!.durMin} min</span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {!hasCheckIn && (
                        <Button variant="primary" size="sm" onClick={handleCheckIn} disabled={cooling || checkInM.isPending}>
                            {cooling || checkInM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />}
                            Check In
                        </Button>
                    )}
                    {hasCheckIn && !hasCheckOut && (
                        <Button variant="primary" size="sm" onClick={handleCheckOut} disabled={cooling || checkOutM.isPending}>
                            {cooling || checkOutM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}
                            Check Out
                        </Button>
                    )}
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
        </Card>
    );
}

function formatTs(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
